// client/src/v2/acervo.tsx
//
// Acervo Forjamente — a estante curada dentro da Explorar.
//
// Por que ela existe: dos 10 cadastros novos de 20/08, oito nunca registraram
// uma leitura. O funil não perde no download, perde no primeiro minuto — e
// "não sei o que ler" é a parede. Uma estante escolhida a dedo dá o que fazer
// nesse minuto.
//
// A página daqui é de EXPOSIÇÃO, não a página pessoal do livro: ela fala do
// livro para quem ainda não tem, enquanto a outra fala do SEU progresso.
//
// Direção de arte: catálogo editorial premium — fundo midnight quente, capa
// tratada como objeto (sombra real, sem brilho mágico), serif display para
// título e mono espaçado para rótulo. A própria capa vira a atmosfera da
// página, desfocada por trás: é o que faz cada livro parecer dele mesmo em
// vez de mais um card num molde.

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Icon } from './icons';
import { openExternalUrl, isNativeApp } from './platform';
import { apiFetchWithSession } from '../services/api';
import { semAcento } from './note-card';

// O apiV2 não exporta a base; aqui ela é literal mesmo, como no resto
// dos módulos que falam direto com o backend.
const API_BASE = '/api';

export interface LivroAcervo {
  slug: string;
  titulo: string;
  autor: string;
  capa: string | null;
  ano?: string | null;
  paginas?: number | null;
  categorias?: string[];
  generos?: string[];
}

export interface LivroAcervoCompleto extends LivroAcervo {
  subtitulo?: string | null;
  descricao?: string | null;
  sobreAutor?: string | null;
  fonteAutor?: string | null;
  idiomaAutor?: string | null;
  editora?: string | null;
  isbn13?: string | null;
  compraUrl?: string | null;
  compraTipo?: string | null;
}

// ─── Capa ─────────────────────────────────────────────────────────────────

const CapaAcervo: React.FC<{
  livro: LivroAcervo; largura: number; onClick?: () => void; elevada?: boolean;
}> = ({ livro, largura, onClick, elevada }) => {
  const [falhou, setFalhou] = useState(false);
  const altura = Math.round(largura * 1.5);   // proporção de lombada, 2:3

  const capa = (
    <div style={{
      width: largura, height: altura, borderRadius: Math.max(5, largura * 0.045),
      overflow: 'hidden', background: 'var(--paper-3)', position: 'relative',
      // Sombra de objeto, não de card: a capa tem que parecer apoiada.
      boxShadow: elevada
        ? '0 26px 48px -18px rgba(0,0,0,0.75), 0 4px 12px -6px rgba(0,0,0,0.5)'
        : '0 12px 22px -12px rgba(0,0,0,0.6)',
    }}>
      {livro.capa && !falhou ? (
        <img
          src={livro.capa} alt={livro.titulo} loading="lazy"
          onError={() => setFalhou(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        // Sem capa a gente desenha uma: buraco cinza no mosaico chama mais
        // atenção que o próprio livro.
        <div style={{
          width: '100%', height: '100%', padding: `${largura * 0.1}px ${largura * 0.085}px`,
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          background: 'linear-gradient(160deg, #241c17, #171210)',
          borderLeft: `${Math.max(2, largura * 0.03)}px solid var(--ember)`,
        }}>
          <div className="serif" style={{
            fontSize: Math.max(11, largura * 0.135), lineHeight: 1.15, color: '#f3ecde',
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
          }}>{livro.titulo}</div>
          <div className="mono" style={{
            fontSize: Math.max(7, largura * 0.072), letterSpacing: '0.08em',
            color: 'rgba(243,236,222,0.5)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{livro.autor}</div>
        </div>
      )}
      {/* Verniz: um brilho diagonal discretíssimo, como luz de estúdio numa
          capa impressa. Sem isso a capa lê como imagem colada. */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'linear-gradient(115deg, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0) 32%)',
      }}/>
      {/* Vinco da lombada, do lado esquerdo */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: Math.max(3, largura * 0.035),
        pointerEvents: 'none',
        background: 'linear-gradient(90deg, rgba(0,0,0,0.34), rgba(0,0,0,0))',
      }}/>
    </div>
  );

  if (!onClick) return capa;
  return (
    <button onClick={onClick} style={{
      width: largura, background: 'none', border: 'none', padding: 0,
      cursor: 'pointer', textAlign: 'left',
    }}>{capa}</button>
  );
};

// ─── Mosaico da Explorar ──────────────────────────────────────────────────

export const AcervoMosaico: React.FC<{
  onAbrir: (slug: string) => void;
  genero?: string;              // 'Tudo' ou vazio = estante inteira
  /** O que foi digitado na busca do Explorar. A caixa de la procurava so
   *  pessoas e clubes; quem digitava o nome de um livro nao achava o livro,
   *  achava no maximo o CLUBE que o estava lendo. */
  busca?: string;
  /** Embaixo da curadoria do Explorar novo: sem o cabeçalho próprio nem o
   *  "Comece por este" — a Temporada, logo acima, já é o começo. */
  naCuradoria?: boolean;
}> = ({ onAbrir, genero, busca, naCuradoria = false }) => {
  const [todos, setTodos] = useState<LivroAcervo[] | null>(null);

  useEffect(() => {
    let vivo = true;
    // A estante inteira. O 60 daqui era de quando o acervo tinha 31 livros e
    // virou teto silencioso: com 232 no catálogo, a tela mostrava 60 e dizia
    // "60" no contador, sem nada indicando que havia mais.
    // Trazer tudo de uma vez é barato — a lista não carrega sinopse nem
    // biografia, e as capas são `loading="lazy"`, então só baixa o que entra
    // na tela.
    fetch('/api/acervo?limite=500')
      .then((r) => r.json())
      .then((d) => { if (vivo) setTodos(d.livros || []); })
      .catch(() => { if (vivo) setTodos([]); });
    return () => { vivo = false; };
  }, []);

  // Filtra no cliente: o payload já veio inteiro e não carrega sinopse nem
  // biografia. Ir ao servidor a cada toque de chip só adicionaria espera.
  const porGenero = !!genero && genero !== 'Tudo';
  // Duas letras: com uma so, "a" traria a estante inteira e a busca pareceria
  // nao ter feito nada.
  const termo = semAcento(busca || '');
  const porTexto = termo.length >= 2;
  const filtrando = porGenero || porTexto;
  const livros = useMemo(() => {
    if (!todos) return null;
    let lista = todos;
    if (porGenero) lista = lista.filter((l) => (l.generos || []).includes(genero!));
    if (porTexto) {
      lista = lista.filter((l) =>
        semAcento(l.titulo).includes(termo)
        || semAcento(l.autor).includes(termo)
        || (l.generos || []).some((g) => semAcento(g).includes(termo)));
    }
    return lista;
  }, [todos, genero, porGenero, porTexto, termo]);

  // ── Quantas capas existem NO DOM ──────────────────────────────────────
  // O JSON dos 232 é barato, mas 232 capas viram 232 elementos com imagem,
  // sombra e gradiente — isso o navegador paga mesmo com `loading="lazy"`,
  // porque lazy adia o download, não a criação do nó nem o cálculo de
  // layout. Então a grade cresce por levas conforme a pessoa desce.
  const LEVA = 16;              // ~4 fileiras na largura de celular
  const [mostrando, setMostrando] = useState(LEVA * 2);
  const sentinela = useRef<HTMLDivElement | null>(null);

  // Trocar de gênero recomeça a contagem: senão, quem tinha descido muito em
  // "Tudo" cairia num recorte de 12 livros já todo renderizado, e quem
  // voltasse para "Tudo" veria 200 capas nascerem de uma vez.
  useEffect(() => { setMostrando(LEVA * 2); }, [genero, termo]);

  useEffect(() => {
    const alvo = sentinela.current;
    if (!alvo) return;
    const obs = new IntersectionObserver((entradas) => {
      // `rootMargin` adianta a carga: a leva seguinte já está pronta quando a
      // borda chega, em vez de aparecer com um solavanco depois.
      if (entradas.some((e) => e.isIntersecting)) setMostrando((m) => m + LEVA);
    }, { rootMargin: '400px' });
    obs.observe(alvo);
    return () => obs.disconnect();
    // `mostrando` na lista de propósito: o IntersectionObserver só avisa quando
    // a interseção MUDA. Se a sentinela continuar na tela depois de a leva
    // entrar — o que acontece em tela grande, onde 16 capas não enchem a
    // altura — ele não dispararia de novo e a lista congelaria no meio.
    // Recriar o observer a cada leva força a reavaliação até a sentinela
    // realmente sair de vista.
  }, [livros, mostrando]);

  // Nada de esqueleto piscando: se o acervo não vier, a seção some.
  if (!livros) return null;

  // MAS QUEM PROCUROU MERECE RESPOSTA.
  //
  // Sumir em silêncio faz sentido quando a seção é um extra que não veio; não
  // faz nenhum quando a pessoa acabou de digitar um nome. Sem isto, buscar um
  // livro que o acervo não tem apagaria a estante inteira da tela, e ela
  // concluiria que a busca quebrou.
  if (!livros.length) {
    if (!porTexto) return null;
    return (
      <div style={{ padding: '18px 18px 8px', textAlign: 'center' }}>
        <div style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          Nenhum livro do acervo com esse nome.
        </div>
      </div>
    );
  }

  // Com filtro ligado, todo mundo entra na prateleira: eleger um "comece por
  // este" dentro de um recorte que a pessoa mesma pediu seria escolher por
  // ela — o destaque existe pra quem chegou sem rumo.
  const destaque = filtrando || naCuradoria ? null : livros[0];
  const resto = destaque ? livros.slice(1) : livros;
  const naTela = resto.slice(0, mostrando);
  const faltam = resto.length - naTela.length;

  return (
    <div style={{ marginTop: 8, paddingBottom: 8 }}>
      {/* Cabeçalho editorial: rótulo, fio, título serif. O fio é o que separa
          "seção do app" de "página de revista". */}
      {!naCuradoria && <div style={{ padding: '0 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span className="mono" style={{
            fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'var(--ember)', fontWeight: 700, flexShrink: 0,
          }}>Ideias de livro, pelo gênero</span>
          {/* SEM CONTADOR, e sem a palavra "acervo" (02/10/2026): "Acervo
              Forjamente · 230" lia como o tamanho de um catálogo para ler aqui
              dentro — a causa nº 1 de quem se cadastrou e não usou. São ideias;
              a pessoa lê no exemplar dela. */}
          <span style={{ flex: 1, height: 1, background: 'var(--line-strong)', opacity: 0.7 }}/>
        </div>
        <div className="serif" style={{ fontSize: 27, lineHeight: 1.08, color: 'var(--ink)' }}>
          {filtrando ? (
            <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{genero}</span>
          ) : (
            <>Escolhidos <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>a dedo</span></>
          )}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.5, maxWidth: 320 }}>
          {filtrando
            ? `${livros.length} ${livros.length === 1 ? 'ideia' : 'ideias'} neste gênero`
            : 'Se você não sabe por onde começar, comece por aqui.'}
          <div data-clareza="ideias">São ideias — você lê no seu exemplar, não aqui dentro.</div>
        </div>
      </div>}

      {/* Destaque: o primeiro da estante ganha vitrine, com a capa ao lado do
          texto. Uma grade uniforme não tem hierarquia — e curadoria sem
          hierarquia é catálogo. */}
      {destaque && (
      <button
        onClick={() => onAbrir(destaque.slug)}
        style={{
          margin: '18px 18px 0', padding: 16, width: 'calc(100% - 36px)',
          display: 'flex', gap: 16, alignItems: 'center', textAlign: 'left',
          borderRadius: 18, cursor: 'pointer',
          background: 'linear-gradient(135deg, rgba(232,122,59,0.10), rgba(232,122,59,0.02))',
          border: '1px solid rgba(232,122,59,0.28)',
        }}>
        <CapaAcervo livro={destaque} largura={92} elevada/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mono" style={{
            fontSize: 8.5, letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'var(--ember)', fontWeight: 700,
          }}>Comece por este</div>
          <div className="serif" style={{
            fontSize: 19, lineHeight: 1.15, color: 'var(--ink)', marginTop: 7,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
          }}>{destaque.titulo}</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 5 }}>{destaque.autor}</div>
          {destaque.paginas && (
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', marginTop: 8, letterSpacing: '0.08em' }}>
              {destaque.paginas} PÁGINAS
            </div>
          )}
        </div>
      </button>
      )}

      {/* O resto em prateleira: capas alinhadas pela base, como numa estante */}
      <div style={{
        marginTop: filtrando ? 16 : 22, padding: '0 18px',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
        gap: '22px 14px', alignItems: 'end',
      }}>
        {naTela.map((l) => (
          <div key={l.slug} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <CapaAcervo livro={l} largura={98} onClick={() => onAbrir(l.slug)}/>
            <div style={{
              fontSize: 10.5, lineHeight: 1.25, color: 'var(--ink-3)',
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>{l.titulo}</div>
          </div>
        ))}
      </div>

      {/* A linha que dispara a próxima leva. Fica FORA da grade: dentro dela
          o `auto-fill` a trataria como mais uma coluna e abriria um buraco. */}
      {faltam > 0 && (
        <div ref={sentinela} style={{
          padding: '26px 18px 4px', textAlign: 'center',
          fontSize: 11.5, color: 'var(--ink-4)', letterSpacing: '0.06em',
        }}>
          {/* Não é botão: o próprio scroll carrega. O texto existe só para a
              rolagem não parecer que acabou enquanto a leva não chega. */}
          mais {faltam} {faltam === 1 ? 'ideia' : 'ideias'} abaixo
        </div>
      )}
    </div>
  );
};

// ─── Página de exposição ──────────────────────────────────────────────────

export const AcervoLivroScreen: React.FC<{
  slug: string;
  session?: any;
  onVoltar: () => void;
  onAdicionarNaEstante?: (livro: LivroAcervoCompleto) => void;
}> = ({ slug, session, onVoltar, onAdicionarNaEstante }) => {
  const [livro, setLivro] = useState<LivroAcervoCompleto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [maisSinopse, setMaisSinopse] = useState(false);

  useEffect(() => {
    let vivo = true;
    setLivro(null); setErro(null);
    fetch('/api/acervo/' + encodeURIComponent(slug))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('não encontrei este livro'))))
      .then((d) => { if (vivo) setLivro(d); })
      .catch((e) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [slug]);

  const comprar = useCallback(() => {
    if (livro?.compraUrl) openExternalUrl(livro.compraUrl);
  }, [livro]);

  if (erro || !livro) {
    return (
      <div className="scroll" style={{ padding: '24px 20px', minHeight: '100vh' }}>
        <Voltar onVoltar={onVoltar}/>
        {erro && <div style={{ marginTop: 40, fontSize: 14, color: 'var(--ink-3)' }}>{erro}</div>}
      </div>
    );
  }

  const sinopse = (livro.descricao || '').trim();
  const longa = sinopse.length > 460;
  const visivel = maisSinopse || !longa ? sinopse : sinopse.slice(0, 460).trimEnd() + '…';
  const ficha = [
    livro.paginas ? { rotulo: 'páginas', valor: String(livro.paginas) } : null,
    livro.ano ? { rotulo: 'ano', valor: livro.ano } : null,
    livro.editora ? { rotulo: 'editora', valor: livro.editora } : null,
  ].filter(Boolean) as { rotulo: string; valor: string }[];

  return (
    /* A folga embaixo soma DUAS barras: a de ação desta tela e a de
       navegação do app (a convenção do app é 110 só pra ela). Sem isso a
       seção de comentários nascia escondida atrás do menu. */
    <div className="scroll" style={{ minHeight: '100vh', paddingBottom: 210, position: 'relative' }}>
      {/* ATMOSFERA — a própria capa, desfocada, vira o fundo do topo. É o que
          faz a página ter a cor do livro em vez de um molde igual pra todos. */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 420, overflow: 'hidden', pointerEvents: 'none' }}>
        {livro.capa ? (
          <img src={livro.capa} alt="" aria-hidden onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{
            width: '130%', height: '130%', marginLeft: '-15%', marginTop: '-15%',
            objectFit: 'cover', filter: 'blur(46px) saturate(1.25)', opacity: 0.5,
          }}/>
        ) : (
          // Sem capa, a atmosfera vem da brasa da marca. O topo chapado deixava
          // esses livros com cara de ficha incompleta — e o que falta é a
          // imagem, não o livro.
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(120% 80% at 50% 0%, rgba(232,122,59,0.30) 0%, rgba(232,122,59,0.10) 42%, transparent 72%)',
          }}/>
        )}
        {/* Sobe pro fundo do app sem emenda visível */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(22,18,16,0.55) 0%, rgba(22,18,16,0.80) 45%, var(--paper) 100%)',
        }}/>
      </div>

      <div style={{ position: 'relative' }}>
        <div style={{ padding: '22px 20px 0' }}>
          <Voltar onVoltar={onVoltar}/>
        </div>

        {/* CAPA EM VITRINE — centralizada, grande, com reflexo. Objeto, não
            miniatura de lista. */}
        <div style={{ padding: '22px 20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CapaAcervo livro={livro} largura={168} elevada/>
          {/* Reflexo no "balcão": a capa espelhada, esmaecendo. */}
          {livro.capa && (
            <div style={{
              width: 168, height: 46, marginTop: 2, overflow: 'hidden', opacity: 0.22,
              maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9), transparent)',
              WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9), transparent)',
              pointerEvents: 'none',
            }}>
              <img src={livro.capa} alt="" aria-hidden onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{
                width: 168, height: 252, objectFit: 'cover',
                transform: 'scaleY(-1)', transformOrigin: 'top', display: 'block',
                marginTop: -206,
              }}/>
            </div>
          )}
        </div>

        {/* TÍTULO */}
        <div style={{ padding: '20px 24px 0', textAlign: 'center' }}>
          <div className="mono" style={{
            fontSize: 8.5, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'var(--ember)', fontWeight: 700,
          }}>Ideia de livro</div>
          <div className="serif" style={{
            fontSize: 30, lineHeight: 1.08, marginTop: 10, color: 'var(--ink)',
            letterSpacing: '-0.01em',
          }}>{livro.titulo}</div>
          {livro.subtitulo && (
            <div className="serif" style={{
              fontSize: 15.5, fontStyle: 'italic', lineHeight: 1.3,
              color: 'var(--ink-3)', marginTop: 8, maxWidth: 300, margin: '8px auto 0',
            }}>{livro.subtitulo}</div>
          )}
          <div style={{ fontSize: 14, color: 'var(--ink-2)', marginTop: 12 }}>{livro.autor}</div>
        </div>

        {/* O FORJAMENTE NÃO ENTREGA O LIVRO (02/10/2026). Esta tela tem sinopse,
            bio do autor e um botão de lista — o comportamento exato de uma loja
            de livros —, e quem chegava aqui saía achando que tinha "guardado um
            livro pra ler no app". O aviso fica fixo, antes de tudo. */}
        <div data-clareza="aviso-sinopse" style={{
          margin: '18px 20px 0', padding: '12px 14px', borderRadius: 12,
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2)', textAlign: 'left',
        }}>
          Este é um livro que outras pessoas leem no Forjamente — no papel, Kindle ou PDF delas.
          A gente não vende nem entrega o conteúdo. Se você já tem este livro (ou vai arranjar um),
          toque em <b>Já tenho / vou ler este</b> pra registrá-lo e entrar no ritmo.
        </div>

        {/* FICHA — blocos com fio entre eles, em vez de uma linha corrida */}
        {ficha.length > 0 && (
          <div style={{
            margin: '22px 20px 0', padding: '14px 0',
            display: 'flex', alignItems: 'stretch',
            borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)',
          }}>
            {ficha.map((f, i) => (
              <div key={f.rotulo} style={{
                flex: 1, minWidth: 0, textAlign: 'center', padding: '0 8px',
                borderLeft: i ? '1px solid var(--line)' : 'none',
              }}>
                <div className="serif" style={{
                  fontSize: 19, color: 'var(--ink)', lineHeight: 1.1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{f.valor}</div>
                <div className="mono" style={{
                  fontSize: 8.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                  color: 'var(--ink-4)', marginTop: 5,
                }}>{f.rotulo}</div>
              </div>
            ))}
          </div>
        )}

        {sinopse && (
          <Secao titulo="Sobre o livro">
            {/* Capitular: marca de página de livro, e dá entrada de leitura. */}
            <p style={{
              margin: 0, fontSize: 15, lineHeight: 1.65, color: 'var(--ink-2)',
              whiteSpace: 'pre-wrap',
            }}>
              <span className="serif" style={{
                float: 'left', fontSize: 46, lineHeight: 0.86, paddingRight: 9, paddingTop: 4,
                color: 'var(--ember)',
              }}>{visivel.slice(0, 1)}</span>
              {visivel.slice(1)}
            </p>
            {longa && (
              <button onClick={() => setMaisSinopse(!maisSinopse)} className="mono" style={{
                clear: 'both', marginTop: 14, background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: 'var(--ember)', display: 'block',
              }}>{maisSinopse ? '— mostrar menos' : '+ ver a sinopse inteira'}</button>
            )}
          </Secao>
        )}

        {livro.sobreAutor && (
          <Secao titulo={'Sobre ' + primeiroNome(livro.autor)}>
            <div style={{
              padding: '16px 18px', borderRadius: 14,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              borderLeft: '3px solid var(--ember)',
            }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.62, color: 'var(--ink-2)' }}>
                {livro.sobreAutor}
              </p>
              {livro.fonteAutor && !isNativeApp() && (
                <a href={livro.fonteAutor} target="_blank" rel="noopener noreferrer" className="mono" style={{
                  display: 'inline-block', marginTop: 12, fontSize: 9,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: 'var(--ink-4)', textDecoration: 'none',
                }}>wikipédia{livro.idiomaAutor === 'en' ? ' · em inglês' : ''}</a>
              )}
            </div>
          </Secao>
        )}

        {session && <BlocoAvaliacoes slug={slug} session={session}/>}
      </div>

      {/* AÇÕES — barra fixa. A decisão ("quero ler") tem que estar sempre a um
          polegar de distância, não no fim de um scroll longo. */}
      <div style={{
        // Acima da barra de navegação, não em cima dela: no `bottom: 0` a
        // barra de ação ficava por baixo do menu do app.
        position: 'fixed', left: 0, right: 0, bottom: 'calc(74px + env(safe-area-inset-bottom))',
        zIndex: 20, padding: '14px 20px 16px',
        display: 'flex', gap: 10, alignItems: 'center',
        background: 'linear-gradient(180deg, rgba(22,18,16,0), var(--paper) 34%)',
        maxWidth: 480, margin: '0 auto',
      }}>
        {onAdicionarNaEstante && (
          <button onClick={() => onAdicionarNaEstante(livro)} style={{
            flex: 1, padding: '15px 18px', borderRadius: 999,
            background: 'var(--ember)', color: 'var(--paper)', border: 'none',
            fontSize: 14.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 14px 28px -10px rgba(199,83,26,0.6)',
          }}>
            <Icon name="plus" size={15} color="var(--paper)" stroke={2.4}/>
            Já tenho / vou ler este
          </button>
        )}
        {livro.compraUrl && (
          <button onClick={comprar} title="Onde comprar" style={{
            width: onAdicionarNaEstante ? 54 : undefined,
            flex: onAdicionarNaEstante ? undefined : 1,
            padding: onAdicionarNaEstante ? '15px 0' : '15px 18px',
            borderRadius: 999, background: 'var(--paper-2)', color: 'var(--ink-2)',
            border: '1px solid var(--line-strong)', fontSize: 13.5, fontWeight: 500,
            fontFamily: 'inherit', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Icon name="arrow-right" size={16} color="var(--ink-2)" stroke={2}/>
            {!onAdicionarNaEstante && 'Onde comprar'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Avaliações ───────────────────────────────────────────────────────────

interface Avaliacoes {
  media: number | null;
  total: number;
  distribuicao: { nota: number; quantas: number }[];
  minha: { nota: number; comentario: string | null } | null;
  comentarios: {
    id: string; nota: number; texto: string; quando: string; souEu: boolean;
    autor: { nome: string; username: string | null; avatar: string | null };
  }[];
}

/** Fileira de estrelas. Vira botão quando `aoEscolher` existe. */
const Estrelas: React.FC<{
  nota: number; tamanho?: number; aoEscolher?: (n: number) => void; opaca?: boolean;
}> = ({ nota, tamanho = 16, aoEscolher, opaca }) => (
  <div style={{ display: 'inline-flex', gap: aoEscolher ? 8 : 2, alignItems: 'center' }}>
    {[1, 2, 3, 4, 5].map((n) => {
      const cheia = n <= Math.round(nota);
      const estrela = (
        <svg width={tamanho} height={tamanho} viewBox="0 0 24 24"
             fill={cheia ? 'var(--ember)' : 'none'}
             stroke={cheia ? 'var(--ember)' : 'var(--ink-4)'} strokeWidth={1.6}
             strokeLinejoin="round" style={{ opacity: opaca && !cheia ? 0.5 : 1, display: 'block' }}>
          <path d="M12 3.2l2.6 5.6 6 .8-4.4 4.2 1.1 6L12 16.9 6.7 19.8l1.1-6L3.4 9.6l6-.8z"/>
        </svg>
      );
      if (!aoEscolher) return <span key={n}>{estrela}</span>;
      return (
        <button key={n} onClick={() => aoEscolher(n)} aria-label={n + ' estrelas'} style={{
          background: 'none', border: 'none', padding: 4, cursor: 'pointer', lineHeight: 0,
        }}>{estrela}</button>
      );
    })}
  </div>
);

function quandoFoi(iso: string): string {
  const d = new Date(iso);
  const dias = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 30) return dias + ' dias atrás';
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
}

const BlocoAvaliacoes: React.FC<{ slug: string; session: any }> = ({ slug, session }) => {
  const [dados, setDados] = useState<Avaliacoes | null>(null);
  const [nota, setNota] = useState(0);
  const [texto, setTexto] = useState('');
  const [abrindo, setAbrindo] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await apiFetchWithSession(session, API_BASE + '/acervo/' + encodeURIComponent(slug) + '/avaliacoes');
      if (!r.ok) { setDados(null); return; }
      const d = await r.json();
      setDados(d);
      if (d.minha) { setNota(d.minha.nota); setTexto(d.minha.comentario || ''); }
    } catch (e) { setDados(null); }
  }, [session, slug]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!nota || salvando) return;
    setSalvando(true);
    try {
      await apiFetchWithSession(session, API_BASE + '/acervo/' + encodeURIComponent(slug) + '/avaliacoes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nota, comentario: texto }),
      });
      setAbrindo(false);
      await carregar();
    } finally { setSalvando(false); }
  };

  if (!dados) return null;

  const jaAvaliei = !!dados.minha;
  const temGente = dados.total > 0;

  return (
    <Secao titulo="O que os leitores acharam">
      {/* Resumo. Com ninguém ainda, some — contador zerado envelhece a página
          e não convida ninguém a ser o primeiro. */}
      {temGente && (
        <div style={{
          display: 'flex', gap: 20, alignItems: 'center',
          padding: '16px 18px', borderRadius: 14,
          background: 'var(--paper-2)', border: '1px solid var(--line)', marginBottom: 16,
        }}>
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div className="serif" style={{ fontSize: 34, lineHeight: 1, color: 'var(--ink)' }}>
              {String(dados.media).replace('.', ',')}
            </div>
            <div style={{ marginTop: 6 }}><Estrelas nota={dados.media || 0} tamanho={12} opaca/></div>
            <div className="mono" style={{ fontSize: 8.5, letterSpacing: '0.12em', color: 'var(--ink-4)', marginTop: 6 }}>
              {dados.total === 1 ? '1 LEITOR' : dados.total + ' LEITORES'}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {dados.distribuicao.map((d) => (
              <div key={d.nota} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mono" style={{ fontSize: 9, color: 'var(--ink-4)', width: 8 }}>{d.nota}</span>
                <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--paper-3)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 3, background: 'var(--ember)',
                    width: (dados.total ? (d.quantas / dados.total) * 100 : 0) + '%',
                  }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Convite / sua nota */}
      <div style={{
        padding: '16px 18px', borderRadius: 14,
        background: jaAvaliei ? 'var(--paper-2)' : 'rgba(232,122,59,0.07)',
        border: '1px solid ' + (jaAvaliei ? 'var(--line)' : 'rgba(232,122,59,0.3)'),
      }}>
        <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginBottom: 12, lineHeight: 1.45 }}>
          {jaAvaliei
            ? 'Sua avaliação'
            : (temGente ? 'Você já leu? Deixe a sua.' : 'Já leu este? Seja o primeiro a comentar.')}
        </div>
        <Estrelas nota={nota} tamanho={26} aoEscolher={(n) => { setNota(n); setAbrindo(true); }}/>

        {abrindo && (
          <div style={{ marginTop: 14 }}>
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="O que este livro te deixou? (opcional)"
              rows={3}
              style={{
                width: '100%', resize: 'vertical', padding: '12px 14px', borderRadius: 12,
                background: 'var(--paper)', border: '1px solid var(--line-strong)',
                color: 'var(--ink)', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setAbrindo(false);
                  setNota(dados.minha ? dados.minha.nota : 0);
                  setTexto(dados.minha && dados.minha.comentario ? dados.minha.comentario : '');
                }}
                className="mono" style={{
                  background: 'none', border: 'none', padding: '9px 12px', cursor: 'pointer',
                  fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)',
                }}>Cancelar</button>
              <button onClick={salvar} disabled={!nota || salvando} style={{
                padding: '10px 20px', borderRadius: 999, background: 'var(--ember)', color: 'var(--paper)',
                border: 'none', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                cursor: salvando ? 'default' : 'pointer', opacity: salvando ? 0.6 : 1,
              }}>{salvando ? 'Salvando…' : 'Publicar'}</button>
            </div>
          </div>
        )}
      </div>

      {/* Comentários */}
      {dados.comentarios.length > 0 && (
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {dados.comentarios.map((c) => (
            <div key={c.id} style={{ display: 'flex', gap: 12 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                background: 'var(--paper-3)', display: 'grid', placeItems: 'center',
              }}>
                {c.autor.avatar
                  ? <img src={c.autor.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
                  : <span className="serif" style={{ fontSize: 15, color: 'var(--ink-3)' }}>{c.autor.nome.slice(0, 1)}</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{c.autor.nome}</span>
                  <Estrelas nota={c.nota} tamanho={11} opaca/>
                  <span className="mono" style={{ fontSize: 9, color: 'var(--ink-4)' }}>{quandoFoi(c.quando)}</span>
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)' }}>{c.texto}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Secao>
  );
};

// ─── Peças ────────────────────────────────────────────────────────────────

const Voltar: React.FC<{ onVoltar: () => void }> = ({ onVoltar }) => (
  <button onClick={onVoltar} aria-label="Voltar" style={{
    width: 40, height: 40, borderRadius: '50%',
    border: '1px solid rgba(243,236,222,0.16)',
    background: 'rgba(22,18,16,0.5)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  }}>
    <Icon name="chevron-left" size={18} color="var(--ink-2)" stroke={2}/>
  </button>
);

/** Rótulo mono + fio + conteúdo. É o ritmo editorial da página. */
const Secao: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div style={{ padding: '30px 20px 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <span className="mono" style={{
        fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase',
        color: 'var(--ink-4)', fontWeight: 600, flexShrink: 0,
      }}>{titulo}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--line)' }}/>
    </div>
    {children}
  </div>
);

function primeiroNome(autor: string): string {
  const p = String(autor || '').trim().split(/\s+/);
  return p.length > 1 ? p[0] + ' ' + p[p.length - 1] : (p[0] || 'o autor');
}
